import { createSession, getClinic } from "api/clinicos";
import { Clinic, Session, Theme, TScreen } from "api/type";
import {
  ClinicContext,
  LanguageContext,
  ScreenContext,
  SessionContext,
  ThemeContext,
} from "contexts";
import React from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
} from "react-router-dom";
import { hexToRgba } from "utils/hexToRgba";
import RootOnboardingTier1 from "forms/onboarding-tier1/RootOnboardingTier1";
import RootOnboardingScan from "forms/onboarding-scan/RootOnboardingScan";
import RootOnboardingAdvertise from "forms/onboarding-advertise/RootOnboardingAdvertise";
import RootOnboardingChatBot from "forms/onboarding-chatbot/RootOnboardingChatBot";
import RootOnboardingScanAllPoses from "forms/onboarding-scan-all-poses/RootOnboardingScanAllPoses";
import RootOnboardingTier1AI from "forms/onboarding-tier1-ai/RootOnboardingTier1AI";
import { NormalText } from "components";
import { useDynamicFont } from "utils/customHook";

type AppContext = {
  session: Session;
  clinic: Clinic;
  theme: Theme;
  // NOTE: Don't put conext with a state here, it will cause our app to re-render
  // TODO: Find out why
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<Outlet />}>
      <Route path="/onboarding-tier1" element={<RootOnboardingTier1 />} />
      <Route path="/onboarding-scan" element={<RootOnboardingScan />} />
      <Route
        path="/onboarding-scan-all-poses"
        element={<RootOnboardingScanAllPoses />}
      />
      <Route
        path="/onboarding-advertise"
        element={<RootOnboardingAdvertise />}
      />
      <Route path="/onboarding-chatbot" element={<RootOnboardingChatBot />} />
      <Route
        path="/onboarding-tier1-chatbot"
        element={<RootOnboardingTier1AI />}
      />
      <Route
        path="*"
        element={<NormalText textContent={"Incorrect form name"} />}
      />
    </Route>,
  ),
);

const FormRouter = () => {
  const [appContext, setAppContext] = React.useState<AppContext | null>(null);
  const [clinicSdkKey, setClinicSdkKey] = React.useState<string | null>(null);
  const [themeColor, setThemeColor] = React.useState<string>("#00B7F4");
  const [isCorrectSdkKey, setIsCorrectSdkKey] = React.useState<boolean>(true);
  const languageContext = React.useState<"en" | "es">("en");
  const screenContext = React.useState<TScreen>(TScreen.NONE);
  useDynamicFont();

  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    const currentUrl = window.location.search;
    const urlSearchParams = new URLSearchParams(currentUrl);

    const urlClinicSdkKey = urlSearchParams.get("clinicSdkKey");
    const urlThemeColor = urlSearchParams.get("themeColor");
    const browserPreferredLanguage = navigator.language.split("-")[0];

    if (browserPreferredLanguage === "es") {
      languageContext[1](browserPreferredLanguage);
    } else {
      languageContext[1]("en");
    }

    if (urlThemeColor) {
      setThemeColor(urlThemeColor);
    }

    if (urlClinicSdkKey) {
      setClinicSdkKey(urlClinicSdkKey);
    }
  }, []);

  React.useEffect(() => {
    const initializeContext = async () => {
      if (clinicSdkKey) {
        try {
          const session = await createSession(clinicSdkKey);
          const clinic = await getClinic(session);
          const theme = { themeColor: themeColor };

          setAppContext({
            session,
            clinic,
            theme,
          });
        } catch (error: unknown) {
          if (error instanceof Error) {
            if (error.message.includes("401")) {
              setIsCorrectSdkKey(false);
            }
          } else {
            console.log("An unexpected error occurred");
          }
        }
      }
    };

    initializeContext();
    document.documentElement.style.setProperty(
      "--theme-color",
      themeColor || "#00B7F4",
    );

    document.documentElement.style.setProperty(
      "--theme-color-opacity",
      `${hexToRgba(themeColor, 0.1)}`,
    );
  }, [clinicSdkKey, themeColor]);

  if (!isCorrectSdkKey) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
        }}
      >
        Check your Clinic SDK Key
      </div>
    );
  }

  if (!appContext) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
        }}
      >
        <div className="loader" />
      </div>
    );
  }

  if (process.env.EXPO_PUBLIC_ENV === "production")
    return (
      <SessionContext.Provider value={appContext.session}>
        <ClinicContext.Provider value={appContext.clinic}>
          <ThemeContext.Provider value={appContext.theme}>
            <LanguageContext.Provider value={languageContext}>
              <ScreenContext.Provider value={screenContext}>
                <RouterProvider router={router} />
              </ScreenContext.Provider>
            </LanguageContext.Provider>
          </ThemeContext.Provider>
        </ClinicContext.Provider>
      </SessionContext.Provider>
    );

  return (
    <React.StrictMode>
      <SessionContext.Provider value={appContext.session}>
        <ClinicContext.Provider value={appContext.clinic}>
          <ThemeContext.Provider value={appContext.theme}>
            <LanguageContext.Provider value={languageContext}>
              <ScreenContext.Provider value={screenContext}>
                <RouterProvider router={router} />
              </ScreenContext.Provider>
            </LanguageContext.Provider>
          </ThemeContext.Provider>
        </ClinicContext.Provider>
      </SessionContext.Provider>
    </React.StrictMode>
  );
};

export default FormRouter;
